import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import Modal from 'react-modal';
import Cookies from 'js-cookie';

import Button from '../../components/Button';
import {
  fetchPageContent,
  getColorVars,
  getDomain,
} from '../../utils/functions';
import Spinner from '../../components/Spinner';
import { DrupalPage } from '../../types/drupal';
import closeIcon from './icon-close.svg';
import Link from '../../components/Link';

function LandingPage(): JSX.Element {
  const domain = getDomain();

  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { t, i18n } = useTranslation(['landing_page']);
  const { language } = i18n;
  const [pageContent, setpageContent] = useState<DrupalPage | null>();

  const baseUrls: { [key: string]: string } = {
    ruka: process.env.REACT_APP_DRUPAL_BASE_URL_RUKA ?? '',
    pyha: process.env.REACT_APP_DRUPAL_BASE_URL_PYHA ?? '',
  };
  const registrationUrl = `${baseUrls[getDomain()]}/${language}/${t(
    'Register'
  ).toLowerCase()}`;

  const faqURls: { [key: string]: string } = {
    ruka: `${process.env.REACT_APP_DRUPAL_BASE_URL_RUKA}/${language}/${process.env.REACT_APP_FAQ_PAGE_URL_RUKA}`,
    pyha: `${process.env.REACT_APP_DRUPAL_BASE_URL_PYHA}/${language}/${process.env.REACT_APP_FAQ_PAGE_URL_PYHA}`,
  };

  const [termsModalIsOpen, setTermsModalIsOpen] = useState(false);
  const [termsModal, setTermsModal] = useState<JSX.Element | null>();
  const [privacyModalIsOpen, setPrivacyModalIsOpen] = useState(false);
  const [privacyModal, setPrivacyModal] = useState<JSX.Element | null>();

  const { textColor } = getColorVars();

  Modal.setAppElement('#root');

  function login() {
    const loginRequest = {
      redirectUri: '/login',
      scopes: ['openid', 'offline_access'],
      extraQueryParameters: { ui_locales: i18n.language },
    };
    instance.loginRedirect(loginRequest);
  }

  // Automatically try to login if the user seems to be logged in
  // in drupal or the url has login query parameter.
  useEffect(() => {
    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      const loggedInDrupal = Cookies.get('drupal_logged_in');

      const currentUrl = new URL(window.location.href);
      const loginParam = currentUrl.searchParams.get('login');

      const redirectUri = currentUrl.searchParams.get('redirect_uri');
      const callbackUrl = currentUrl.searchParams.get('callback_url') ?? '';

      if (redirectUri === 'iticket') {
        window.sessionStorage.setItem('postLoginRedirect', 'iticket');
        window.sessionStorage.setItem('callback_url', callbackUrl);
      }

      if (loggedInDrupal || loginParam) {
        login();
      }
    }
  }, [inProgress]);

  useEffect(() => {
    async function getPageContent() {
      const pageId: { [key: string]: string } = {};
      pageId['ruka'] = process.env.REACT_APP_LANDING_PAGE_RUKA ?? '';
      pageId['pyha'] = process.env.REACT_APP_LANDING_PAGE_PYHA ?? '';
      const data = await fetchPageContent(pageId[domain], language);
      setpageContent(data);

      // Close the modal and empty the content when the language
      // changes, so the content will be fetched again for the
      // new language.
      setTermsModal(null);
      setTermsModalIsOpen(false);
      setPrivacyModal(null);
      setPrivacyModalIsOpen(false);
    }

    getPageContent();
  }, [i18n.language]);

  async function openModal(page: string) {
    switch (page) {
      case 'terms':
        setTermsModalIsOpen(!termsModalIsOpen);
        if (!termsModal) {
          const pageId: { [key: string]: string } = {};
          pageId['ruka'] = process.env.REACT_APP_TERMS_CONDITIONS_RUKA ?? '';
          pageId['pyha'] = process.env.REACT_APP_TERMS_CONDITIONS_PYHA ?? '';
          const data = await fetchPageContent(pageId[domain], language);
          if (data) {
            const modal = generateModalContent(data, page);
            setTermsModal(modal);
          }
        }
        break;
      case 'privacy':
        setPrivacyModalIsOpen(!privacyModalIsOpen);
        if (!privacyModal) {
          const pageId: { [key: string]: string } = {};
          pageId['ruka'] = process.env.REACT_APP_PRIVACY_POLICY_RUKA ?? '';
          pageId['pyha'] = process.env.REACT_APP_PRIVACY_POLICY_PYHA ?? '';
          const data = await fetchPageContent(pageId[domain], language);
          if (data) {
            const modal = generateModalContent(data, page);
            setPrivacyModal(modal);
          }
        }
        break;
    }
  }

  function generateModalContent(data: DrupalPage, page: string): JSX.Element {
    return (
      <div>
        <span className='flex justify-between'>
          <h2>{data?.title}</h2>
          <Link
            href='#'
            onClick={() =>
              page === 'terms'
                ? setTermsModalIsOpen(false)
                : setPrivacyModalIsOpen(false)
            }
          >
            <img src={closeIcon} />
          </Link>
        </span>
        {data.field_content_text && (
          <div
            className='my-5'
            dangerouslySetInnerHTML={{
              __html: data.field_content_text,
            }}
          ></div>
        )}
      </div>
    );
  }

  return (
    <div className='px-5 my-16 md:px-40 lg:px-80'>
      {pageContent ? (
        <>
          {pageContent.title && (
            <h1 className='text-2xl text-center uppercase'>
              {pageContent.field_content_title ?? pageContent.title}
            </h1>
          )}

          {pageContent.field_content_text && (
            <p
              className='my-5 md:text-center'
              dangerouslySetInnerHTML={{
                __html: pageContent.field_content_text,
              }}
            ></p>
          )}

          <div className='mt-16'>
            <Button onClick={login} className='w-full'>
              {t('Login') ?? ''}
            </Button>
          </div>

          <div className='mt-4'>
            <Button href={registrationUrl} className='w-full'>
              {t('Register') ?? ''}
            </Button>
          </div>

          <div className='flex flex-col items-end mt-16'>
            <a
              href='#'
              onClick={() => openModal('privacy')}
              className={textColor}
            >
              {t('Privacy policy') ?? ''}
            </a>

            <Modal
              isOpen={termsModalIsOpen}
              style={{ content: { top: '90px' } }}
              onRequestClose={() => setTermsModalIsOpen(false)}
            >
              {termsModal ? termsModal : <Spinner />}
            </Modal>
            <a
              href='#'
              onClick={() => openModal('terms')}
              className={textColor}
            >
              {t('Terms of service') ?? ''}
            </a>
            <Modal
              isOpen={privacyModalIsOpen}
              style={{ content: { top: '90px' } }}
              onRequestClose={() => setPrivacyModalIsOpen(false)}
            >
              {privacyModal ? privacyModal : <Spinner />}
            </Modal>

            {faqURls[domain] && (
              <a
                href={faqURls[domain]}
                target='_blank'
                rel='nofollow noreferrer'
                className={textColor}
              >
                {t('Frequently asked questions') ?? ''}
              </a>
            )}
          </div>
        </>
      ) : (
        <div className='mt-8'>
          <Spinner />
        </div>
      )}
    </div>
  );
}

export default LandingPage;
